import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, doc, addDoc, getDoc, updateDoc, getDocs, deleteDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, limitToLast,
} from "firebase/firestore";
import { IC, Avatar, useToast } from "../shared";

// ── Relative time helper ─────────────────────────────────────────────────────
const timeAgo = (date) => {
  if (!date) return "";
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  if (diff < 604800) return `${Math.floor(diff / 86400)} days`;
  return date.toLocaleDateString("en", { month: "short", day: "numeric" });
};

// ── Chat Screen ───────────────────────────────────────────────────────────────
const TWELVE_HOURS = 12 * 60 * 60 * 1000;

const ChatScreen = ({ currentUser, openRequest, onOpenRequestHandled, onActiveChange, openLostChat, onLostChatHandled }) => {
  const toast = useToast();
  const [passengerChats,       setPassengerChats]       = useState([]);
  const [driverChats,          setDriverChats]          = useState([]);
  const [lostItemChats,        setLostItemChats]        = useState([]);
  const [active,               setActive]               = useState(null);
  const [messages,             setMessages]             = useState([]);
  const [msg,                  setMsg]                  = useState("");
  const [unreadByChatId,       setUnreadByChatId]       = useState({});  // chatId → [notifId, ...]
  const [lastMessages,         setLastMessages]         = useState({});  // chatId → { text, ts, senderId }
  const [, tick] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    const q1 = query(collection(db, "requests"), where("passengerId", "==", currentUser.id));
    const q2 = query(collection(db, "requests"), where("driverId",    "==", currentUser.id));
    const filterActive = (docs) => {
      const now = new Date();
      const active = [];
      const completed = [];
      docs.forEach(d => {
        const r = { id: d.id, ...d.data() };
        if (r.status !== "accepted") return;
        if (new Date(r.tripDatetime) <= now) { completed.push(r); return; }
        active.push(r);
      });
      // Clean up chat messages for completed trips
      completed.forEach(r => {
        getDocs(collection(db, "chats", r.id, "messages")).then(snap => {
          snap.docs.forEach(m => deleteDoc(doc(db, "chats", r.id, "messages", m.id)));
        }).catch(() => {});
      });
      return active;
    };
    const unsub1 = onSnapshot(q1, snap => setPassengerChats(filterActive(snap.docs)));
    const unsub2 = onSnapshot(q2, snap => setDriverChats(filterActive(snap.docs)));
    // Track unread message notifications per chat
    const q3 = query(collection(db, "notifications", currentUser.id, "items"), where("type", "==", "message"));
    const unsub3 = onSnapshot(q3, snap => {
      const byChat = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.chatId && !data.read) {
          if (!byChat[data.chatId]) byChat[data.chatId] = [];
          byChat[data.chatId].push(d.id);
        }
      });
      setUnreadByChatId(byChat);
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [currentUser.id]);

  // Listen to lost-item chats (as passenger or driver)
  useEffect(() => {
    const q1 = query(collection(db, "lostItemChats"), where("passengerId", "==", currentUser.id));
    const q2 = query(collection(db, "lostItemChats"), where("driverId", "==", currentUser.id));
    const merge = (snap1, snap2) => {
      const now = Date.now();
      const all = [...snap1, ...snap2];
      const seen = new Set();
      return all.filter(c => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        const created = c.createdAt?.toDate?.()?.getTime() || 0;
        return c.status === "active" && (now - created) < TWELVE_HOURS;
      });
    };
    let docs1 = [], docs2 = [];
    const unsub1 = onSnapshot(q1, snap => {
      docs1 = snap.docs.map(d => ({ id: d.id, ...d.data(), _lostItem: true }));
      setLostItemChats(merge(docs1, docs2));
    });
    const unsub2 = onSnapshot(q2, snap => {
      docs2 = snap.docs.map(d => ({ id: d.id, ...d.data(), _lostItem: true }));
      setLostItemChats(merge(docs1, docs2));
    });
    // Refresh timer every second for live countdown
    const iv = setInterval(() => tick(x => x + 1), 1000);
    return () => { unsub1(); unsub2(); clearInterval(iv); };
  }, [currentUser.id]);

  // Auto-open lost-item chat when navigated from ForgotItem screen
  useEffect(() => {
    if (openLostChat?.chatId) {
      const chat = lostItemChats.find(c => c.id === openLostChat.chatId);
      if (chat) { setActive(chat); onActiveChange?.(true); }
      else {
        // Chat might not be loaded yet, set directly
        setActive({ id: openLostChat.chatId, _lostItem: true, ...openLostChat });
        onActiveChange?.(true);
      }
      onLostChatHandled?.();
    }
  }, [openLostChat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to the latest message for each chat
  const allChats = [...passengerChats, ...driverChats, ...lostItemChats];
  const chatIdsKey = allChats.map(c => c.id).sort().join(",");
  useEffect(() => {
    if (allChats.length === 0) return;
    const unsubs = allChats.map(chat => {
      const colPath = chat._lostItem ? "lostItemChats" : "chats";
      const q = query(
        collection(db, colPath, chat.id, "messages"),
        orderBy("ts", "desc"),
        limitToLast(1)
      );
      return onSnapshot(q, snap => {
        if (snap.docs.length > 0) {
          const d = snap.docs[0].data();
          setLastMessages(prev => ({ ...prev, [chat.id]: { text: d.text, ts: d.ts, senderId: d.senderId } }));
        }
      });
    });
    return () => unsubs.forEach(u => u());
  }, [chatIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open a specific chat when navigated from Trips screen
  useEffect(() => {
    if (openRequest) {
      openChat(openRequest);
      onOpenRequestHandled?.();
    }
  }, [openRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const openChat = async (chat) => {
    if (chat._lostItem) {
      setActive(chat);
      onActiveChange?.(true);
      const notifIds = unreadByChatId[chat.id] || [];
      notifIds.forEach(nid => updateDoc(doc(db, "notifications", currentUser.id, "items", nid), { read: true }));
      return;
    }
    // Re-verify request status before opening — prevents reading messages after rejection/cancellation
    const reqSnap = await getDoc(doc(db, "requests", chat.id));
    if (!reqSnap.exists() || reqSnap.data().status !== "accepted") {
      toast("This chat is no longer available.", "info");
      return;
    }
    setActive(chat);
    onActiveChange?.(true);
    const notifIds = unreadByChatId[chat.id] || [];
    notifIds.forEach(nid => updateDoc(doc(db, "notifications", currentUser.id, "items", nid), { read: true }));
  };

  useEffect(() => {
    if (!active) { setMessages([]); return; }
    const colPath = active._lostItem ? "lostItemChats" : "chats";
    const q = query(collection(db, colPath, active.id, "messages"), orderBy("ts", "asc"));
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    return unsub;
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMsg = async () => {
    if (!msg.trim() || !active) return;
    const text = msg.trim(); setMsg("");
    const colPath = active._lostItem ? "lostItemChats" : "chats";
    await addDoc(collection(db, colPath, active.id, "messages"), { senderId: currentUser.id, text, ts: serverTimestamp() });
    const otherId = active.passengerId === currentUser.id ? active.driverId : active.passengerId;
    await addDoc(collection(db, "notifications", otherId, "items"), {
      text: active._lostItem ? `${currentUser.name} sent a message about a lost item` : `New message from ${currentUser.name}`,
      tab: "chat", type: active._lostItem ? "lost_item" : "message", chatId: active.id,
      ts: serverTimestamp(), read: false,
    });
  };

  // Auto-close active chat if request is no longer accepted (e.g. cancelled while chatting)
  useEffect(() => {
    if (active && !allChats.find(c => c.id === active.id)) {
      setActive(null);
    }
  }, [allChats, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const getOther = chat => chat.passengerId === currentUser.id
    ? { id: chat.driverId,    name: chat.driverName,    avatar: chat.driverAvatar,    rating: chat.driverRating || 0 }
    : { id: chat.passengerId, name: chat.passengerName, avatar: chat.passengerAvatar, rating: chat.passengerRating || 0 };

  const closeChat = () => { setActive(null); onActiveChange?.(false); };

  // Lost-item chat timer
  const getLostItemTimer = (chat) => {
    if (!chat?._lostItem || !chat?.createdAt) return null;
    const created = chat.createdAt?.toDate?.()?.getTime() || (chat.createdAt?.seconds ? chat.createdAt.seconds * 1000 : 0);
    if (!created) return null;
    const remaining = TWELVE_HOURS - (Date.now() - created);
    if (remaining <= 0) return "0:00:00";
    const hrs = Math.floor(remaining / 3600000);
    const mins = Math.floor((remaining % 3600000) / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const isDriver = active?.driverId === currentUser.id;

  if (active) {
    const other = getOther(active);
    const timer = getLostItemTimer(active);
    return (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: "flex", flexDirection: "column", background: "var(--paper)" }} className="achat-in">
        {/* Header */}
        <div style={{ paddingTop: "env(safe-area-inset-top, 12px)", background: "var(--card)", borderBottom: active._lostItem ? "none" : "1px solid var(--border)", boxShadow: "0 1px 4px rgba(15,14,19,.06)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px 12px" }}>
            <button onClick={closeChat} style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--warm)", borderRadius: 12, border: "none", cursor: "pointer", flexShrink: 0 }}>
              <span style={{ width: 18, height: 18, color: "var(--ink)" }}>{IC.back}</span>
            </button>
            <div style={{ position: "relative" }}>
              <Avatar user={other} size={38}/>
              {!active._lostItem && <div style={{ position: "absolute", bottom: 0, right: 0, width: 12, height: 12, borderRadius: "50%", background: "var(--success)", border: "2px solid var(--card)" }}/>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{other.name}</div>
              <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>{active.tripFrom} → {active.tripTo}</div>
            </div>
            {!active._lostItem && <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--success)", boxShadow: "0 0 0 3px rgba(15,158,104,.2)" }}/>}
          </div>
        </div>

        {/* Lost-item banner */}
        {active._lostItem && (
          <div style={{ padding: "12px 16px", background: "linear-gradient(135deg, rgba(245,166,35,.12), rgba(245,166,35,.06))", borderBottom: "1px solid rgba(245,166,35,.2)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(245,166,35,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f5a623" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontFamily: "Syne", fontWeight: 700, color: "#f5a623" }}>
                {isDriver ? "Forgot an Item" : "Temporary chat"}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>
                {isDriver ? "Your passenger may have left something in your car" : "This chat closes automatically"}
              </div>
            </div>
            {timer && <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 14, color: "#f5a623" }}>{timer}</div>}
          </div>
        )}
        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, marginTop: 50 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>👋</div>
              <div style={{ fontFamily: "Syne", fontWeight: 600 }}>Say hi!</div>
            </div>
          )}
          {messages.map(m => {
            const mine = m.senderId === currentUser.id;
            return (
              <div key={m.id} className="abubble" style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}>
                {!mine && <Avatar user={other} size={24}/>}
                <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: mine ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: mine ? "linear-gradient(135deg, #2b5be0, #5a80f0)" : "var(--card)", color: mine ? "#fff" : "var(--ink)", border: mine ? "none" : "1.5px solid var(--border)", fontSize: 14, lineHeight: 1.45, boxShadow: mine ? "0 4px 14px rgba(43,91,224,.3)" : "var(--shadow)", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                  {m.text}
                  <div style={{ fontSize: 10, color: mine ? "rgba(255,255,255,.4)" : "var(--muted)", marginTop: 4, textAlign: mine ? "right" : "left" }}>
                    {m.ts?.toDate ? m.ts.toDate().toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" }) : "…"}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef}/>
        </div>
        {/* Input */}
        <div style={{ padding: "10px 16px calc(16px + env(safe-area-inset-bottom, 0px))", background: "var(--nav-blur)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderTop: "1px solid var(--border)", display: "flex", gap: 10, flexShrink: 0 }}>
          <input value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMsg()} placeholder="Type a message…" style={{ flex: 1, padding: "12px 16px", borderRadius: 14, border: "1.5px solid var(--border)", background: "var(--card)", fontSize: 14, color: "var(--ink)", outline: "none" }}/>
          <button onClick={sendMsg} style={{ width: 46, height: 46, borderRadius: 13, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 12px rgba(43,91,224,.3)", border: "none", cursor: "pointer" }}>
            <span style={{ width: 18, height: 18 }}>{IC.send}</span>
          </button>
        </div>
      </div>
    );
  }

  // Sort chats: unread first, then by latest message time
  const sortedChats = [...allChats].sort((a, b) => {
    const ua = unreadByChatId[a.id]?.length || 0;
    const ub = unreadByChatId[b.id]?.length || 0;
    if (ua && !ub) return -1;
    if (!ua && ub) return 1;
    const ta = lastMessages[a.id]?.ts?.toDate?.()?.getTime() || 0;
    const tb = lastMessages[b.id]?.ts?.toDate?.()?.getTime() || 0;
    return tb - ta;
  });

  return (
    <div className="screen" style={{ padding: "20px 16px 90px" }}>
      <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--ink)", marginBottom: 16 }}>Messages</h2>
      {sortedChats.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px 0", color: "var(--muted)" }}>
          <div style={{ fontSize: 48 }}>💬</div>
          <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, marginTop: 12 }}>No chats yet</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Accepted requests unlock chat</div>
        </div>
      ) : sortedChats.map(chat => {
          const other = getOther(chat);
          const unreadCount = unreadByChatId[chat.id]?.length || 0;
          const last = lastMessages[chat.id];
          const lastText = last?.text || null;
          const lastTime = last?.ts?.toDate ? timeAgo(last.ts.toDate()) : "";
          return (
            <div key={chat.id} onClick={() => openChat(chat)} className="aslide"
              style={{
                padding: "14px 0",
                borderBottom: "1px solid var(--border)",
                cursor: "pointer",
                display: "flex", alignItems: "flex-start", gap: 14,
              }}>
              {/* Avatar with badge */}
              <div style={{ position: "relative", flexShrink: 0, marginTop: 2 }}>
                <Avatar user={other} size={48}/>
                {chat._lostItem ? (
                  <div style={{ position: "absolute", top: -4, right: -4, width: 22, height: 22, borderRadius: 8, background: "#f5a623", border: "2.5px solid var(--paper)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                ) : (
                  <div style={{ position: "absolute", bottom: 1, right: 1, width: 12, height: 12, borderRadius: "50%", background: "var(--success)", border: "2.5px solid var(--paper)" }}/>
                )}
              </div>
              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Name + time row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{other.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", flexShrink: 0, marginLeft: 8 }}>{lastTime}</div>
                </div>
                {/* Route + lost item pill */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 13, color: "var(--accent)", fontWeight: 600 }}>{chat.tripFrom} → {chat.tripTo}</span>
                  {chat._lostItem && (
                    <span style={{ fontSize: 9, fontFamily: "Syne", fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: "rgba(245,166,35,.12)", color: "#f5a623", textTransform: "uppercase", letterSpacing: ".03em" }}>Lost Item</span>
                  )}
                </div>
                {/* Last message preview */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {lastText && <span style={{ width: 6, height: 6, borderRadius: "50%", background: unreadCount ? "var(--accent)" : "var(--muted)", flexShrink: 0, opacity: unreadCount ? 1 : 0.5 }}/>}
                  <div style={{
                    fontSize: 13, color: unreadCount ? "var(--ink)" : "var(--muted)",
                    fontWeight: unreadCount ? 600 : 400,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {lastText || "No messages yet"}
                  </div>
                </div>
              </div>
              {/* Unread badge */}
              {unreadCount > 0 && (
                <div style={{
                  background: "var(--accent)", color: "#fff", borderRadius: 12,
                  fontSize: 11, fontWeight: 700, fontFamily: "Syne",
                  padding: "3px 9px", minWidth: 22, textAlign: "center",
                  flexShrink: 0, marginTop: 2,
                }}>
                  {unreadCount}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default ChatScreen;
